import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FileText, Plus, Search, ExternalLink, Download,
  CheckCircle, Clock, AlertCircle, Send, DollarSign,
  Receipt, Eye, Loader2, Link as LinkIcon
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const QuotesInvoicesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  // Create New Charge Modal
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [chargeType, setChargeType] = useState(""); // "booking" or "adhoc"
  const [creating, setCreating] = useState(false);
  
  // View Detail Modal
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Ad-hoc charge form state
  const [newCharge, setNewCharge] = useState({
    client_email: "",
    client_name: "",
    package_name: "",
    line_items: [{ description: "", amount: "", quantity: 1 }],
    notes: "",
    booking_id: "" // Optional - to link to existing booking
  });

  useEffect(() => {
    fetchData();
    
    // Check for URL params to pre-fill form
    const bookingId = searchParams.get("booking");
    const clientName = searchParams.get("name");
    const clientEmail = searchParams.get("email");
    
    if (bookingId) {
      setNewCharge(prev => ({
        ...prev,
        booking_id: bookingId,
        client_name: clientName || "",
        client_email: clientEmail || ""
      }));
      setChargeType("adhoc");
      setShowCreateDialog(true);
      // Clear URL params
      setSearchParams({});
    }
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, invoicesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/quotes/all`, { withCredentials: true }),
        axios.get(`${API}/invoices/all`, { withCredentials: true }),
        axios.get(`${API}/bookings/requests`, { withCredentials: true })
      ]);
      setQuotes(quotesRes.data || []);
      setInvoices(invoicesRes.data || []);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setNewCharge({
      ...newCharge,
      line_items: [...newCharge.line_items, { description: "", amount: "", quantity: 1 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...newCharge.line_items];
    updated[index][field] = value;
    setNewCharge({ ...newCharge, line_items: updated });
  };

  const removeLineItem = (index) => {
    if (newCharge.line_items.length > 1) {
      const updated = newCharge.line_items.filter((_, i) => i !== index);
      setNewCharge({ ...newCharge, line_items: updated });
    }
  };

  const calculateTotal = () => {
    return newCharge.line_items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const qty = parseInt(item.quantity) || 1;
      return sum + (amount * qty);
    }, 0);
  };

  const createAdHocCharge = async () => {
    // Validate
    if (!newCharge.client_email || !newCharge.client_name) {
      toast.error("Please enter client email and name");
      return;
    }
    
    if (!newCharge.package_name) {
      toast.error("Please enter a description for this charge");
      return;
    }
    
    const validItems = newCharge.line_items.filter(
      item => item.description && parseFloat(item.amount) > 0
    );
    
    if (validItems.length === 0) {
      toast.error("Please add at least one line item with description and amount");
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API}/quotes/adhoc`,
        {
          quote_type: "AD_HOC",
          client_email: newCharge.client_email,
          client_name: newCharge.client_name,
          package_name: newCharge.package_name,
          line_items: validItems.map(item => ({
            description: item.description,
            unit_price: parseFloat(item.amount),
            quantity: parseInt(item.quantity) || 1
          })),
          deliverables: newCharge.notes || newCharge.package_name,
          deposit_percentage: 0,
          due_immediately: true,
          booking_id: newCharge.booking_id || null
        },
        { withCredentials: true }
      );
      
      toast.success("Charge created and sent to client!");
      setShowCreateDialog(false);
      setChargeType("");
      resetForm();
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create charge");
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewCharge({
      client_email: "",
      client_name: "",
      package_name: "",
      line_items: [{ description: "", amount: "", quantity: 1 }],
      notes: "",
      booking_id: ""
    });
  };

  const openViewDialog = (item) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const downloadQuotePdf = async (quoteId, bookingId) => {
    setDownloadingPdf("quote");
    try {
      const response = await axios.get(
        `${API}/quotes/booking/${bookingId}/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to download quote PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const downloadInvoicePdf = async (invoiceId, bookingId) => {
    setDownloadingPdf("invoice");
    try {
      const response = await axios.get(
        `${API}/bookings/request/${bookingId}/invoice-pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const getQuoteStatusBadge = (status) => {
    const styles = {
      draft: { class: "bg-gray-500/20 text-gray-400 border-gray-500/30", icon: FileText },
      sent: { class: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Send },
      viewed: { class: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Eye },
      accepted: { class: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle },
      paid: { class: "bg-green-500/20 text-green-400 border-green-500/30", icon: DollarSign },
      declined: { class: "bg-red-500/20 text-red-400 border-red-500/30", icon: AlertCircle },
      expired: { class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Clock }
    };
    const style = styles[status] || styles.draft;
    const Icon = style.icon;
    return (
      <Badge className={`${style.class} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getInvoiceStatusBadge = (status) => {
    const styles = {
      paid: { class: "bg-green-500/20 text-green-400 border-green-500/30", label: "Paid" },
      pending: { class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Pending" },
      overdue: { class: "bg-red-500/20 text-red-400 border-red-500/30", label: "Overdue" }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={`${style.class} border`}>{style.label}</Badge>;
  };

  // Combine and filter data
  const getFilteredData = () => {
    let combined = [];
    
    // Add quotes with their linked invoices
    quotes.forEach(quote => {
      const linkedInvoice = invoices.find(inv => inv.quote_id === quote.quote_id);
      combined.push({
        type: "quote",
        data: quote,
        linkedInvoice,
        searchText: `${quote.package_name} ${quote.client_name || ''} ${quote.client_email || ''}`.toLowerCase(),
        date: quote.created_at
      });
    });
    
    // Add standalone invoices (not linked to quotes)
    invoices.filter(inv => !inv.quote_id).forEach(invoice => {
      combined.push({
        type: "invoice",
        data: invoice,
        searchText: `${invoice.client_name} ${invoice.client_email}`.toLowerCase(),
        date: invoice.created_at
      });
    });
    
    // Sort by date
    combined.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Filter by search
    if (searchQuery) {
      combined = combined.filter(item => 
        item.searchText.includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by tab
    if (activeTab === "quotes") {
      combined = combined.filter(item => item.type === "quote");
    } else if (activeTab === "invoices") {
      combined = combined.filter(item => item.type === "invoice" || item.linkedInvoice);
    } else if (activeTab === "adhoc") {
      combined = combined.filter(item => 
        item.type === "quote" && item.data.quote_type === "AD_HOC"
      );
    }
    
    return combined;
  };

  const filteredData = getFilteredData();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="quotes-invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif tracking-tight mb-2">Quotes & Invoices</h1>
          <p className="text-muted-foreground">
            Manage quotes, invoices, and one-time charges
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="btn-primary" data-testid="create-new-charge-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create New Charge
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="quotes">Quotes</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="adhoc">Ad-Hoc Charges</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by client name, email, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* List */}
      {filteredData.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg mb-2">No {activeTab === "all" ? "quotes or invoices" : activeTab} yet</p>
            <p className="text-muted-foreground mb-4">
              Create a quote from a booking request or add a new charge
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create New Charge
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredData.map((item, index) => (
            <motion.div
              key={item.data.quote_id || item.data.invoice_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-medium">
                          {item.type === "quote" 
                            ? item.data.package_name 
                            : item.data.package_name || "Invoice"
                          }
                        </p>
                        {item.type === "quote" && getQuoteStatusBadge(item.data.status)}
                        {item.linkedInvoice && getInvoiceStatusBadge(item.linkedInvoice.status)}
                        {item.type === "invoice" && !item.linkedInvoice && getInvoiceStatusBadge(item.data.status)}
                        {item.data.quote_type === "AD_HOC" && (
                          <Badge variant="outline" className="text-xs">Ad-Hoc</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.data.client_name || "Client"} • {item.data.client_email}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span>Created: {new Date(item.data.created_at).toLocaleDateString()}</span>
                        {item.data.booking_id && (
                          <span className="flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" />
                            Linked to booking
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right mr-4">
                      <p className="text-xl font-semibold">${item.data.total?.toFixed(2)}</p>
                      {item.type === "quote" && item.data.deposit_amount > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Deposit: ${item.data.deposit_amount?.toFixed(2)}
                        </p>
                      )}
                      {item.linkedInvoice && (
                        <p className="text-xs text-green-500">
                          Balance: ${item.linkedInvoice.balance_due?.toFixed(2)}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openViewDialog(item)}
                        data-testid={`view-${item.data.quote_id || item.data.invoice_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {item.data.booking_id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            if (item.type === "quote") {
                              downloadQuotePdf(item.data.quote_id, item.data.booking_id);
                            } else {
                              downloadInvoicePdf(item.data.invoice_id, item.data.booking_id);
                            }
                          }}
                          disabled={downloadingPdf !== null}
                        >
                          {downloadingPdf ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          PDF
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create New Charge Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) {
          setChargeType("");
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Charge</DialogTitle>
            <DialogDescription>
              {!chargeType 
                ? "What are you charging for?"
                : chargeType === "booking" 
                  ? "Create a quote for a booking session"
                  : "Create a one-time charge for immediate payment"
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Step 1: Select charge type */}
          {!chargeType && (
            <div className="py-6">
              <RadioGroup value={chargeType} onValueChange={setChargeType} className="space-y-4">
                <div 
                  className="flex items-center space-x-4 p-4 border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setChargeType("booking")}
                >
                  <RadioGroupItem value="booking" id="booking" />
                  <div className="flex-1">
                    <Label htmlFor="booking" className="text-base font-medium cursor-pointer">
                      Booking / Session
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Standard quote with deposit & balance due before event
                    </p>
                  </div>
                </div>
                
                <div 
                  className="flex items-center space-x-4 p-4 border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => setChargeType("adhoc")}
                >
                  <RadioGroupItem value="adhoc" id="adhoc" />
                  <div className="flex-1">
                    <Label htmlFor="adhoc" className="text-base font-medium cursor-pointer">
                      One-time Charge
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Add-on, rush delivery, gallery purchase, etc. - 100% payment required immediately
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Step 2: Booking Quote - Redirect to requests page */}
          {chargeType === "booking" && (
            <div className="py-6 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg mb-2">Create from Booking Request</p>
              <p className="text-muted-foreground mb-6">
                Standard booking quotes are created from the Requests page. 
                This ensures proper calendar blocking and deposit workflows.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setChargeType("")}>
                  Back
                </Button>
                <Button onClick={() => window.location.href = "/dashboard/requests"}>
                  Go to Requests
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Ad-Hoc Charge Form */}
          {chargeType === "adhoc" && (
            <div className="space-y-6 py-4">
              {/* Info Banner */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  💡 <strong>One-time Charge:</strong> Payment is required in full immediately. 
                  No deposit workflow - client receives invoice for immediate payment.
                </p>
              </div>

              {/* Link to existing booking (optional) */}
              <div className="space-y-2">
                <Label>Link to Booking (Optional)</Label>
                <Select 
                  value={newCharge.booking_id || "none"}
                  onValueChange={(value) => {
                    const bookingId = value === "none" ? "" : value;
                    setNewCharge({ ...newCharge, booking_id: bookingId });
                    // Auto-fill client info if booking selected
                    if (bookingId) {
                      const booking = bookings.find(b => b.request_id === bookingId);
                      if (booking) {
                        setNewCharge(prev => ({
                          ...prev,
                          booking_id: bookingId,
                          client_name: booking.customer_name,
                          client_email: booking.customer_email
                        }));
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None - Standalone charge" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None - Standalone charge</SelectItem>
                    {bookings.filter(b => b.status !== "declined").map(booking => (
                      <SelectItem key={booking.request_id} value={booking.request_id}>
                        {booking.customer_name} - {booking.event_type} ({booking.event_date || "TBD"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Client Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    placeholder="John Doe"
                    value={newCharge.client_name}
                    onChange={(e) => setNewCharge({ ...newCharge, client_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Email *</Label>
                  <Input
                    type="email"
                    placeholder="client@email.com"
                    value={newCharge.client_email}
                    onChange={(e) => setNewCharge({ ...newCharge, client_email: e.target.value })}
                  />
                </div>
              </div>

              {/* Charge Description */}
              <div className="space-y-2">
                <Label>Charge Description *</Label>
                <Input
                  placeholder="e.g., Rush delivery fee, Extra edits, Album upgrade..."
                  value={newCharge.package_name}
                  onChange={(e) => setNewCharge({ ...newCharge, package_name: e.target.value })}
                />
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <Label>Line Items</Label>
                {newCharge.line_items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, "description", e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={item.amount}
                      onChange={(e) => updateLineItem(index, "amount", e.target.value)}
                      className="w-28"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                      className="w-20"
                      min="1"
                    />
                    {newCharge.line_items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Additional notes for the client..."
                  value={newCharge.notes}
                  onChange={(e) => setNewCharge({ ...newCharge, notes: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div>
                  <span className="font-medium">Total Due Now</span>
                  <p className="text-xs text-muted-foreground">100% payment required</p>
                </div>
                <span className="text-2xl font-bold">${calculateTotal().toFixed(2)}</span>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setChargeType("")}>
                  Back
                </Button>
                <Button onClick={createAdHocCharge} disabled={creating} className="btn-primary">
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invoice for Immediate Payment
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              {selectedItem?.type === "quote" ? (
                <>
                  <FileText className="w-6 h-6" />
                  Quote Details
                </>
              ) : (
                <>
                  <Receipt className="w-6 h-6" />
                  Invoice Details
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-lg font-medium">{selectedItem.data.package_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedItem.data.client_name} • {selectedItem.data.client_email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedItem.type === "quote" && getQuoteStatusBadge(selectedItem.data.status)}
                  {selectedItem.linkedInvoice && getInvoiceStatusBadge(selectedItem.linkedInvoice.status)}
                  {selectedItem.type === "invoice" && getInvoiceStatusBadge(selectedItem.data.status)}
                  {selectedItem.data.quote_type === "AD_HOC" && (
                    <Badge variant="outline">Ad-Hoc</Badge>
                  )}
                </div>
              </div>

              {/* Line Items */}
              {selectedItem.data.line_items?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                    Line Items
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-center">Qty</th>
                          <th className="px-4 py-2 text-right">Price</th>
                          <th className="px-4 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedItem.data.line_items.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{item.description}</td>
                            <td className="px-4 py-2 text-center">{item.quantity}</td>
                            <td className="px-4 py-2 text-right">${item.unit_price?.toFixed(2)}</td>
                            <td className="px-4 py-2 text-right">${(item.quantity * item.unit_price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-semibold">${selectedItem.data.total?.toFixed(2)}</span>
                  </div>
                  {selectedItem.type === "quote" && selectedItem.data.deposit_amount > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Deposit ({selectedItem.data.deposit_percentage}%)</span>
                        <span>${selectedItem.data.deposit_amount?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Balance Due</span>
                        <span>${selectedItem.data.balance_due?.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {selectedItem.linkedInvoice && (
                    <div className="flex justify-between pt-2 border-t">
                      <span className="font-medium">Invoice Balance</span>
                      <span className="text-lg font-bold">
                        ${selectedItem.linkedInvoice.balance_due?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Deliverables */}
              {selectedItem.data.deliverables && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Deliverables
                  </h4>
                  <p className="text-sm">{selectedItem.data.deliverables}</p>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Created:</span>{" "}
                  {new Date(selectedItem.data.created_at).toLocaleDateString()}
                </div>
                {selectedItem.data.sent_at && (
                  <div>
                    <span className="text-muted-foreground">Sent:</span>{" "}
                    {new Date(selectedItem.data.sent_at).toLocaleDateString()}
                  </div>
                )}
                {selectedItem.data.expires_at && (
                  <div>
                    <span className="text-muted-foreground">Expires:</span>{" "}
                    {new Date(selectedItem.data.expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
                {selectedItem.data.booking_id && (
                  <Button
                    onClick={() => {
                      if (selectedItem.type === "quote") {
                        downloadQuotePdf(selectedItem.data.quote_id, selectedItem.data.booking_id);
                      } else {
                        downloadInvoicePdf(selectedItem.data.invoice_id, selectedItem.data.booking_id);
                      }
                    }}
                    disabled={downloadingPdf !== null}
                  >
                    {downloadingPdf ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesInvoicesPage;
